/**
 * Selectable UI guide modes — shared/ui/guide-modes.json
 */

import guideJson from "../../../shared/ui/guide-modes.json";

export type HelpDensity = "full" | "tooltip" | "hidden";

export type GuideChrome = {
  hideStory?: boolean;
  collapsePanel?: boolean;
  largeStage?: boolean;
  hideLabOnly?: boolean;
};

export type GuideMode = {
  id: string;
  label: string;
  short: string;
  description: string;
  helpDensity: HelpDensity;
  showEquationBreakdown: boolean;
  showInteractions: boolean;
  showLowHigh: boolean;
  defaultOpenSections: string[];
  chrome?: GuideChrome;
};

type GuideFile = {
  version: number;
  default: string;
  modes: GuideMode[];
};

const file = guideJson as GuideFile;

export const GUIDE_MODES: GuideMode[] = file.modes;
export const DEFAULT_GUIDE_MODE_ID: string = file.default;

const byId = new Map(GUIDE_MODES.map((m) => [m.id, m]));

export function getGuideMode(id: string | null | undefined): GuideMode {
  if (id && byId.has(id)) return byId.get(id)!;
  return byId.get(DEFAULT_GUIDE_MODE_ID) ?? GUIDE_MODES[0]!;
}

export function listGuideModes(): GuideMode[] {
  return [...GUIDE_MODES];
}

const PANEL_OPEN_KEY = "showoff.kioskPanelOpen";

/** Read last kiosk panel open/closed preference (default open). */
export function getKioskPanelOpen(): boolean {
  try {
    const v = sessionStorage.getItem(PANEL_OPEN_KEY);
    if (v === "0") return false;
    if (v === "1") return true;
  } catch {
    /* ignore */
  }
  return true;
}

export function setKioskPanelOpen(open: boolean): void {
  try {
    sessionStorage.setItem(PANEL_OPEN_KEY, open ? "1" : "0");
  } catch {
    /* ignore */
  }
  applyPanelOpenAttr(open);
  syncPanelToggleButton(open);
}

function applyPanelOpenAttr(open: boolean): void {
  document.documentElement.dataset.panelOpen = open ? "1" : "0";
}

function syncPanelToggleButton(open: boolean): void {
  const btn = document.getElementById(
    "btn-panel-toggle",
  ) as HTMLButtonElement | null;
  if (!btn) return;
  btn.setAttribute("aria-expanded", open ? "true" : "false");
  btn.textContent = open ? "Hide panel" : "Show panel";
  btn.title = open
    ? "Collapse side panel for a larger stage"
    : "Expand side panel (presets & guide)";
}

let toggleBound = false;

function ensurePanelToggleBound(): void {
  if (toggleBound) return;
  const btn = document.getElementById("btn-panel-toggle");
  if (!btn) return;
  toggleBound = true;
  btn.addEventListener("click", () => {
    const open = document.documentElement.dataset.panelOpen !== "0";
    setKioskPanelOpen(!open);
    // Resize observers on the stage pick up the new grid track
    window.dispatchEvent(new Event("resize"));
  });
}

/**
 * Apply DOM chrome hooks for kiosk / large-stage modes.
 * Sets attributes on BOTH html and body so CSS selectors stay consistent.
 */
export function applyGuideChrome(mode: GuideMode): void {
  const chrome = mode.chrome ?? {};
  const isKiosk = mode.id === "kiosk";
  const root = document.documentElement;
  const body = document.body;

  root.dataset.guide = mode.id;
  root.dataset.kiosk = isKiosk ? "1" : "0";
  root.dataset.collapsePanel = chrome.collapsePanel ? "1" : "0";
  root.dataset.largeStage = chrome.largeStage ? "1" : "0";
  root.dataset.hideLab = chrome.hideLabOnly ? "1" : "0";
  root.dataset.hideStory = chrome.hideStory ? "1" : "0";

  body.dataset.guide = mode.id;
  body.dataset.help = mode.helpDensity;
  body.dataset.kiosk = isKiosk ? "1" : "0";

  const panel = document.querySelector(".panel") as HTMLElement | null;
  if (panel) {
    panel.classList.toggle("panel-collapsed", !!chrome.collapsePanel);
    panel.classList.toggle("is-kiosk", isKiosk);
  }

  const story = document.getElementById("preset-story");
  // Tagline in topbar: hide in kiosk (story lives in panel / blurb)
  if (story) {
    const hideTopTagline = isKiosk || !!chrome.hideStory;
    story.classList.toggle("hidden", hideTopTagline);
  }

  // Lab / advanced sections hidden in kiosk (keep Basic + preset rail only)
  const labSections = [
    '[data-section="model"]',
    '[data-section="topology"]',
    '[data-section="advanced"]',
    '[data-section="relations"]',
    '[data-section="camera"]',
    '[data-section="lenses"]',
  ];
  for (const sel of labSections) {
    document.querySelectorAll(sel).forEach((el) => {
      (el as HTMLElement).hidden = !!(chrome.hideLabOnly || isKiosk);
    });
  }

  ensurePanelToggleBound();
  if (isKiosk) {
    const open = getKioskPanelOpen();
    applyPanelOpenAttr(open);
    syncPanelToggleButton(open);
  } else {
    // Leave kiosk: always restore full panel track
    applyPanelOpenAttr(true);
    syncPanelToggleButton(true);
  }

  // Nudge layout after grid template change
  requestAnimationFrame(() => {
    window.dispatchEvent(new Event("resize"));
  });
}
